import { Controller } from '@hotwired/stimulus'

interface TurboFrameElement extends HTMLElement {
  src: string | null
  reload(): void
}

interface JsI18n {
  exists(key: string): boolean
  t(key: string): string
}

const MESSAGE_KEY = 'js.results.frame_error.message'
const MESSAGE_FALLBACK = 'These results could not be loaded. Check your connection and try again.'
const RETRY_KEY = 'js.results.frame_error.retry'
const RETRY_FALLBACK = 'Try again'

// Loads a results turbo-frame slightly BEFORE it scrolls into view, so the
// page feels fluid: the frame is rendered without a src and this controller
// assigns it once the frame comes within `margin` of the viewport
// (default: one full viewport ahead).
//
// A failed load leaves the frame with no content and no poller inside to
// recover, so this controller re-arms the load with backoff; once maxFailures is
// reached it stops and swaps in a translated retry state. The counter lives on
// the frame's own dataset, beside the poller's.
//
// Failure has TWO signals. A silent one (dropped connection, empty-bodied
// response such as `head :forbidden`) never dispatches turbo:frame-load, so a
// watchdog covers it; `timeout` is deliberately far above a healthy fragment
// response, since reload() supersedes the in-flight fetch and a slow-but-alive
// request then costs one retry, not a pile-up. And a loud one: Turbo dispatches
// turbo:frame-load for error responses too, after BLANKING the frame (an error
// page has no matching frame, so it renders an empty one), which is why an empty
// frame counts as a failure instead of a settled load.
export default class extends Controller<TurboFrameElement> {
  static values = {
    src: String,
    margin: { type: String, default: '100%' },
    timeout: { type: Number, default: 20000 },
    backoff: { type: Number, default: 1.4 },
    maxFailures: { type: Number, default: 5 },
  }

  declare readonly srcValue: string
  declare readonly marginValue: string
  declare readonly timeoutValue: number
  declare readonly backoffValue: number
  declare readonly maxFailuresValue: number

  private observer?: IntersectionObserver
  private timer?: ReturnType<typeof setTimeout>
  private readonly onFrameLoad = (): void => this.frameLoaded()

  connect(): void {
    this.element.addEventListener('turbo:frame-load', this.onFrameLoad)
    this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
      rootMargin: `${this.marginValue} 0px`,
    })
    this.observer.observe(this.element)
  }

  disconnect(): void {
    this.observer?.disconnect()
    this.element.removeEventListener('turbo:frame-load', this.onFrameLoad)
    if (this.timer) clearTimeout(this.timer)
  }

  retry(): void {
    this.errorElement()?.remove()
    this.revealSkeleton()
    delete this.element.dataset.lazyLoadFailures
    this.load()
  }

  private onIntersect(entries: IntersectionObserverEntry[]): void {
    if (!entries.some((entry) => entry.isIntersecting)) return

    this.observer?.disconnect()
    this.load()
  }

  private load(): void {
    if (this.element.src) this.element.reload()
    else this.element.src = this.srcValue

    this.timer = setTimeout(() => this.fail(0), this.backoffDelay())
  }

  // Content arrived: the frame now owns its own refresh (poller/refresher).
  // An EMPTY frame is an error response Turbo rendered as blank content, so it
  // is a failure — and the one that has to be retried after the backoff, since
  // the server answered immediately.
  private frameLoaded(): void {
    if (this.timer) clearTimeout(this.timer)
    if (this.loadedEmpty()) return this.fail(this.backoffDelay())

    delete this.element.dataset.lazyLoadFailures
  }

  // Count the attempt and either give up (translated retry state) or try again:
  // with no delay when the watchdog already waited out the backoff, after it
  // when the failure came back fast.
  private fail(retryDelay: number): void {
    const failures = this.failures() + 1
    this.element.dataset.lazyLoadFailures = String(failures)

    if (failures >= this.maxFailuresValue) return this.renderError()

    this.timer = setTimeout(() => this.load(), retryDelay)
  }

  private backoffDelay(): number {
    return this.timeoutValue * this.backoffValue ** this.failures()
  }

  // Our own error box does not count as content.
  private loadedEmpty(): boolean {
    const children = Array.from(this.element.children) as HTMLElement[]
    return children.every((child) => child.dataset.resultsFrameError === 'true')
  }

  private renderError(): void {
    if (this.errorElement()) return

    Array.from(this.element.children).forEach((child) => { (child as HTMLElement).hidden = true })

    const box = document.createElement('div')
    box.className = 'results-processing-banner'
    box.setAttribute('role', 'alert')
    box.dataset.resultsFrameError = 'true'

    const message = document.createElement('p')
    message.textContent = translate(MESSAGE_KEY, MESSAGE_FALLBACK)

    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'btn btn--blue-stroke btn--small'
    button.dataset.action = `${this.identifier}#retry`
    button.textContent = translate(RETRY_KEY, RETRY_FALLBACK)

    box.append(message, button)
    this.element.append(box)
  }

  private revealSkeleton(): void {
    Array.from(this.element.children).forEach((child) => { (child as HTMLElement).hidden = false })
  }

  private errorElement(): HTMLElement | null {
    return this.element.querySelector('[data-results-frame-error]')
  }

  private failures(): number {
    return Number(this.element.dataset.lazyLoadFailures || 0)
  }
}

// Client-side strings go through the i18n-js bundle, falling back to English
// for locales Phrase has not translated yet (same shape as chart_controller).
function translate(key: string, fallback: string): string {
  const i18n = (window as unknown as { I18n?: JsI18n }).I18n
  return i18n?.exists?.(key) ? i18n.t(key) : fallback
}
